import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AutoTradeDisabledModal from './AutoTradeDisabledModal';
import { AUTOTRADE_STRATEGY_OPTIONS } from '../constants/autotradeStrategies';

const SPHERE_SRC = '/assets/autotrade/sphare_automate_button.svg';

function formatAutotradeActivityTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  } catch {
    return String(iso);
  }
}

function counterPillClass(code) {
  const c = String(code || '');
  if (c.startsWith('zone_reversal_skip_')) {
    return 'bg-amber-100/90 text-amber-950 border-amber-200';
  }
  if (c.startsWith('zone_reversal_allow_') || c.startsWith('zone_reversal_recheck_')) {
    return 'bg-emerald-50 text-emerald-900 border-emerald-100';
  }
  if (c.startsWith('zone_reversal_pending_')) {
    return 'bg-blue-50 text-blue-900 border-blue-100';
  }
  if (
    c === 'breakeven_sl_updated' ||
    c === 'strategy_trail_updated' ||
    c === 'ma_ribbon_trail_updated' ||
    c === 'config_updated'
  ) {
    return 'bg-indigo-50 text-indigo-900 border-indigo-100';
  }
  if (
    c === 'skipped_option_side_filter' ||
    c === 'skipped_session_profit_limit' ||
    c === 'skipped_session_loss_limit'
  ) {
    return 'bg-orange-50 text-orange-900 border-orange-100';
  }
  if (c === 'ma_ribbon_trail_skipped' || c === 'ma_ribbon_forced_exit') {
    return 'bg-violet-50 text-violet-900 border-violet-100';
  }
  if (c.startsWith('skipped_') || c === 'order_failed' || c === 'order_placed_no_exit_gtt') {
    return 'bg-amber-50 text-amber-900 border-amber-100';
  }
  if (c === 'order_placed') {
    return 'bg-emerald-50 text-emerald-900 border-emerald-100';
  }
  return 'bg-slate-100 text-slate-800 border-slate-200';
}

function exitGttLook(status) {
  const s = String(status || 'none').toLowerCase();
  if (s === 'attached') {
    return {
      label: 'Attached',
      chipClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    };
  }
  if (s === 'pending_worker') {
    return {
      label: 'Pending worker',
      chipClass: 'bg-blue-50 text-blue-800 border-blue-200',
    };
  }
  if (s === 'fallback') {
    return {
      label: 'Fallback',
      chipClass: 'bg-amber-50 text-amber-900 border-amber-200',
    };
  }
  return {
    label: 'None',
    chipClass: 'bg-slate-100 text-slate-700 border-slate-200',
  };
}

function autotradeActivityLook(code) {
  if (code === 'order_placed') {
    return {
      wrap: 'bg-emerald-50/95 border-emerald-100',
      title: 'Order placed',
      titleClass: 'text-emerald-800',
    };
  }
  if (code === 'order_placed_no_exit_gtt') {
    return {
      wrap: 'bg-red-50/95 border-red-100',
      title: 'Manual exit required',
      titleClass: 'text-red-800',
    };
  }
  if (code === 'order_failed') {
    return {
      wrap: 'bg-red-50/95 border-red-100',
      title: 'Order not placed',
      titleClass: 'text-red-800',
    };
  }
  if (code && String(code).startsWith('skipped_')) {
    return {
      wrap: 'bg-amber-50/95 border-amber-100',
      title: 'No order',
      titleClass: 'text-amber-900',
    };
  }
  if (code) {
    return {
      wrap: 'bg-slate-50 border-slate-100',
      title: 'Last activity',
      titleClass: 'text-slate-800',
    };
  }
  return {
    wrap: 'bg-slate-50 border-slate-100',
    title: 'Last activity',
    titleClass: 'text-slate-700',
  };
}

/**
 * Auto Trade — layout matches web reference: white card, sphere CTA, hint, status, history button.
 */
function AutoTradePanel({
  autoTradeEnabled,
  autoTradeStatus,
  autoTradeLoading,
  statusLoading,
  autoTradeLots,
  setAutoTradeLots,
  autoTradeExitSource,
  setAutoTradeExitSource,
  autoTradeSignalTargetKey,
  setAutoTradeSignalTargetKey,
  autoTradeCustomTargetPrice,
  setAutoTradeCustomTargetPrice,
  autoTradeCustomStopLossPrice,
  setAutoTradeCustomStopLossPrice,
  autoTradeStrategyExitConfig = {},
  updateStrategyExitField = () => {},
  autoTradeOptionSideFilter = 'BOTH',
  setAutoTradeOptionSideFilter = () => {},
  autoTradeProfitLimitInr = '',
  setAutoTradeProfitLimitInr = () => {},
  autoTradeLossLimitInr = '',
  setAutoTradeLossLimitInr = () => {},
  autoTradeStrikeMode,
  setAutoTradeStrikeMode,
  autoTradeExpiryChoice,
  setAutoTradeExpiryChoice,
  autoTradeAllStrategies = true,
  setAutoTradeAllStrategies = () => {},
  autoTradeSelectedStrategies = [],
  toggleAutoTradeStrategy = () => {},
  toggleAutomate,
  actionError,
  showAutoTradeDisabledModal,
  setShowAutoTradeDisabledModal,
  autoTradeDisabledReason,
  autoTradeModalCtaLabel,
  configSaving = false,
  saveAutoTradeConfig = async () => {},
}) {
  const navigate = useNavigate();
  const [configOpen, setConfigOpen] = useState(false);
  const [configSaveMessage, setConfigSaveMessage] = useState('');

  const handleConfigDone = async () => {
    if (autoTradeEnabled) {
      const result = await saveAutoTradeConfig();
      if (result?.ok) {
        setConfigSaveMessage(
          result.openOrdersUpdated > 0
            ? `Saved. ${result.openOrdersUpdated} open order(s) refreshed.`
            : 'Configuration saved.',
        );
        setConfigOpen(false);
      }
      return;
    }
    setConfigOpen(false);
  };

  const lastActivityLook =
    autoTradeEnabled && autoTradeStatus?.last_activity_summary
      ? autotradeActivityLook(autoTradeStatus.last_activity_code)
      : null;
  const manualExitRequired = Boolean(autoTradeEnabled && autoTradeStatus?.manual_exit_required);
  const manualExitRequiredCount = autoTradeStatus?.manual_exit_required_count || 0;
  const activityCountersToday = Array.isArray(autoTradeStatus?.activity_counters_today)
    ? autoTradeStatus.activity_counters_today.filter((row) => Number(row?.count) > 0)
    : [];
  const exitGtt = exitGttLook(autoTradeStatus?.exit_gtt_status);
  const sessionPnl = Number(autoTradeStatus?.total_profit_loss ?? 0);
  const ordersPausedByPnl = Boolean(autoTradeEnabled && autoTradeStatus?.orders_paused_by_pnl);

  const activeStrategyKeys = autoTradeAllStrategies
    ? AUTOTRADE_STRATEGY_OPTIONS.map((o) => o.value)
    : autoTradeSelectedStrategies;
  const strategyExitConfigValid = activeStrategyKeys.every((key) => {
    const cfg = autoTradeStrategyExitConfig[key];
    return cfg && Number(cfg.target_points) >= 1 && Number(cfg.stop_loss_points) >= 1;
  });

  const sphereDisabled =
    autoTradeLoading ||
    statusLoading ||
    (!autoTradeEnabled &&
      (autoTradeLots < 1 ||
        !strategyExitConfigValid ||
        (!autoTradeAllStrategies && autoTradeSelectedStrategies.length === 0)));

  return (
    <div className="w-full bg-white rounded-[1.25rem] border border-gray-200/80 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.12)] px-6 sm:px-10 md:px-12 pt-5 sm:pt-7 pb-8 sm:pb-10">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/nifty-signal"
          className="text-sm font-semibold text-gray-600 hover:text-gray-900 shrink-0"
        >
          ← Back to NIFTY Signal
        </Link>
        <button
          type="button"
          onClick={() => {
            setConfigSaveMessage('');
            setConfigOpen(true);
          }}
          className="w-10 h-10 shrink-0 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          aria-label="Open trading configuration"
        >
          <svg className="w-[1.15rem] h-[1.15rem]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col items-center pt-8 sm:pt-10 pb-2">
        <div
          className={`relative rounded-full transition-transform ${autoTradeLoading ? 'scale-[0.98]' : ''}`}
          style={{ width: 'min(260px, 78vw)', height: 'min(260px, 78vw)' }}
        >
          <button
            type="button"
            onClick={() => void toggleAutomate()}
            disabled={sphereDisabled}
            className={`relative w-full h-full rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              autoTradeEnabled
                ? 'focus-visible:ring-red-500'
                : 'focus-visible:ring-emerald-500'
            }`}
          >
            <img
              src={SPHERE_SRC}
              alt=""
              className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              draggable={false}
            />
            {autoTradeEnabled ? (
              <span className="relative z-10 flex flex-col items-center justify-center text-center leading-[1.05] pointer-events-none px-1">
                <span className="text-[clamp(0.72rem,3.2vw,1.05rem)] font-extrabold tracking-[0.1em] text-red-600">
                  STOP
                </span>
                <span className="text-[clamp(0.72rem,3.2vw,1.05rem)] font-extrabold tracking-[0.1em] text-red-600">
                  AUTOMATE
                </span>
                <span className="text-[clamp(0.72rem,3.2vw,1.05rem)] font-extrabold tracking-[0.1em] text-red-600">
                  TRADE
                </span>
              </span>
            ) : (
              <span className="relative z-10 flex flex-col items-center justify-center text-center leading-[1.08] pointer-events-none px-2">
                <span className="text-[clamp(0.85rem,3.8vw,1.22rem)] font-extrabold tracking-[0.14em] text-[#119B70]">
                  AUTOMATE
                </span>
                <span className="text-[clamp(0.85rem,3.8vw,1.22rem)] font-extrabold tracking-[0.14em] text-[#119B70]">
                  TRADE
                </span>
              </span>
            )}
            {autoTradeLoading ? (
              <span className="absolute right-5 top-5 z-20 w-7 h-7 rounded-full bg-white/80 flex items-center justify-center">
                <svg
                  className={`animate-spin h-4 w-4 ${autoTradeEnabled ? 'text-red-600' : 'text-emerald-600'}`}
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : null}
          </button>
        </div>

        <div className="mt-10 w-full min-w-0 flex justify-center overflow-x-auto">
          <p className="text-center text-xs sm:text-sm text-gray-500 whitespace-nowrap px-2">
            {autoTradeEnabled
              ? 'Tap the button above to stop automated trading.'
              : 'Activate automated trade by clicking on above button.'}
          </p>
        </div>
      </div>

      {autoTradeEnabled && autoTradeStatus && (
        <div className="mt-4 mb-1 px-1 py-3 rounded-xl bg-gray-50/90 border border-gray-100">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs text-gray-700">
            <span>
              <span className="font-medium text-gray-600">Trades placed:</span> {autoTradeStatus.total_trades_placed || 0}
            </span>
            <span>
              <span className="font-medium text-gray-600">Active orders:</span> {autoTradeStatus.active_orders || 0}
            </span>
          </div>
          <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-center text-gray-700">
            <div>
              <span className="text-gray-500">Target:</span>{' '}
              <span className="font-semibold text-green-600">{autoTradeStatus.target_points || 10} pts</span>
            </div>
            <div>
              <span className="text-gray-500">Stop-loss:</span>{' '}
              <span className="font-semibold text-red-600">{autoTradeStatus.stop_loss_points || 10} pts</span>
            </div>
            <div>
              <span className="text-gray-500">Successful:</span>{' '}
              <span className="font-semibold text-green-600">{autoTradeStatus.total_trades_successful || 0}</span>
            </div>
            <div>
              <span className="text-gray-500">Stopped:</span>{' '}
              <span className="font-semibold text-red-600">{autoTradeStatus.total_trades_stopped || 0}</span>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-gray-600 text-center">
            Exit mode:{' '}
            <span className="font-semibold text-gray-800">
              {autoTradeStatus.exit_source === 'signal_targets' ? 'Signal targets' : 'Session points'}
            </span>
            {autoTradeStatus.exit_source === 'signal_targets' ? (
              <>
                {' · '}Target key:{' '}
                <span className="font-semibold text-gray-800">
                  {(autoTradeStatus.signal_target_key || 'target_1').replace('_', ' ').toUpperCase()}
                </span>
              </>
            ) : null}
          </div>
          <div className="mt-2 text-[11px] text-gray-600 text-center">
            Exit GTT:{' '}
            <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${exitGtt.chipClass}`}>
              {exitGtt.label}
            </span>
            {autoTradeStatus?.exit_gtt_status_note ? (
              <span className="ml-2 text-gray-500">{autoTradeStatus.exit_gtt_status_note}</span>
            ) : null}
          </div>
        </div>
      )}

      {lastActivityLook && autoTradeStatus?.last_activity_summary && (
        <div className={`mt-3 px-3 py-3 rounded-xl border text-left ${lastActivityLook.wrap}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
            <p className={`text-xs font-bold uppercase tracking-wide ${lastActivityLook.titleClass}`}>
              {lastActivityLook.title}
            </p>
            {autoTradeStatus.last_activity_at ? (
              <p className="text-[11px] text-gray-500 shrink-0">
                {formatAutotradeActivityTime(autoTradeStatus.last_activity_at)}
              </p>
            ) : null}
          </div>
          <p className="text-sm text-gray-800 leading-snug">{autoTradeStatus.last_activity_summary}</p>
          {autoTradeStatus.last_activity_code ? (
            <p className="mt-2 text-[10px] text-gray-400 font-mono">{autoTradeStatus.last_activity_code}</p>
          ) : null}
          {activityCountersToday.length > 0 ? (
            <div className="mt-3 pt-3 border-t border-black/5">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                Today&apos;s tuning counters (IST)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activityCountersToday.map((row) => (
                  <span
                    key={row.code}
                    title={row.code}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] leading-tight ${counterPillClass(row.code)}`}
                  >
                    <span className="font-medium">{row.label}</span>
                    <span className="font-bold tabular-nums">×{row.count}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!lastActivityLook && activityCountersToday.length > 0 ? (
        <div className="mt-3 px-3 py-3 rounded-xl border border-slate-200 bg-slate-50/90 text-left">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-600 mb-2">
            Today&apos;s tuning counters (IST)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {activityCountersToday.map((row) => (
              <span
                key={row.code}
                title={row.code}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] leading-tight ${counterPillClass(row.code)}`}
              >
                <span className="font-medium">{row.label}</span>
                <span className="font-bold tabular-nums">×{row.count}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {ordersPausedByPnl && (
        <div className="mt-3 px-4 py-3 rounded-xl border border-orange-200 bg-orange-50/95 text-left">
          <p className="text-xs font-bold uppercase tracking-wide text-orange-800">New orders paused</p>
          <p className="mt-1 text-sm text-orange-900 leading-snug">
            {autoTradeStatus?.orders_paused_reason ||
              'Session profit or loss limit reached. Existing trades are still monitored.'}
          </p>
        </div>
      )}

      {manualExitRequired && (
        <div className="mt-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50/95 text-left">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wide text-red-800">
                Manual SL/Target Required
                {manualExitRequiredCount > 0 ? ` · ${manualExitRequiredCount} order${manualExitRequiredCount > 1 ? 's' : ''}` : ''}
              </p>
              <p className="mt-1 text-sm text-red-900 leading-snug">
                {autoTradeStatus?.last_activity_summary ||
                  'Entry was placed, but SL/Target GTT could not be attached. Place stop-loss and target manually immediately.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/auto-trade-orders')}
              className="shrink-0 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors"
            >
              Open Order History
            </button>
          </div>
        </div>
      )}

      {autoTradeEnabled && autoTradeStatus?.insufficient_balance_error && (
        <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-800 text-center">
          <p className="font-medium">Insufficient balance</p>
          <p className="mt-1 text-xs">{autoTradeStatus.insufficient_balance_error}</p>
        </div>
      )}

      {actionError ? (
        <div className="mt-3 p-2.5 rounded-lg border border-red-100 bg-red-50/90 text-red-800 text-sm text-center">
          {actionError}
        </div>
      ) : null}

      <div className="mt-6 sm:mt-8 w-full">
        <p className="text-center text-sm text-gray-700 mb-4">
          <span className="font-semibold text-gray-800">Status:</span>{' '}
          {statusLoading ? 'Checking…' : autoTradeEnabled ? 'Active' : 'Inactive'}
        </p>
        <button
          type="button"
          onClick={() => navigate('/auto-trade-orders')}
          className="flex w-full max-w-md mx-auto items-center justify-center gap-2 px-4 py-3.5 bg-[#2b6be6] hover:bg-[#2563c4] text-white rounded-xl transition-colors text-sm font-semibold shadow-sm"
        >
          <span aria-hidden>📊</span>
          View Order History & Statistics
        </button>
      </div>

      {configOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/35"
          role="dialog"
          aria-modal="true"
          aria-labelledby="autotrade-config-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfigOpen(false);
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 id="autotrade-config-title" className="text-base font-extrabold text-gray-900">
                Trading configuration
              </h2>
              <button
                type="button"
                onClick={() => setConfigOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="at-lots" className="block text-xs font-bold text-gray-600 mb-1">
                  Number of Lots
                </label>
                <input
                  id="at-lots"
                  type="number"
                  min={1}
                  max={100}
                  value={autoTradeLots}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10) || 1;
                    setAutoTradeLots(Math.max(1, Math.min(100, v)));
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
                />
              </div>

              <div className="pt-1 border-t border-gray-100">
                <div className="block text-xs font-bold text-gray-600 mb-2">Direction filter</div>
                <label htmlFor="at-option-side" className="block text-xs font-semibold text-gray-600 mb-1">
                  Place orders for
                </label>
                <select
                  id="at-option-side"
                  value={autoTradeOptionSideFilter}
                  onChange={(e) => setAutoTradeOptionSideFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  <option value="BOTH">Both CE and PE signals</option>
                  <option value="CE">CE only (bullish / up view)</option>
                  <option value="PE">PE only (bearish / down view)</option>
                </select>
                <p className="mt-1.5 text-[11px] text-gray-500">
                  Use CE-only when you expect NIFTY to move up; PE-only when you expect a fall.
                </p>
              </div>

              <div className="pt-1 border-t border-gray-100">
                <div className="block text-xs font-bold text-gray-600 mb-2">Session P&amp;L limits</div>
                <p className="text-[11px] text-gray-500 mb-2">
                  Stop placing <span className="font-semibold">new</span> orders when today&apos;s realized session
                  P&amp;L crosses your limit. Open trades keep running.
                  {autoTradeEnabled ? (
                    <>
                      {' '}
                      Current P&amp;L:{' '}
                      <span
                        className={
                          sessionPnl > 0
                            ? 'font-semibold text-emerald-700'
                            : sessionPnl < 0
                              ? 'font-semibold text-red-700'
                              : 'font-semibold text-gray-700'
                        }
                      >
                        ₹{sessionPnl.toFixed(2)}
                      </span>
                    </>
                  ) : null}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="at-profit-limit" className="block text-xs font-semibold text-gray-600 mb-1">
                      Max profit (INR)
                    </label>
                    <input
                      id="at-profit-limit"
                      type="number"
                      min={1}
                      step={100}
                      placeholder="e.g. 2000"
                      value={autoTradeProfitLimitInr}
                      onChange={(e) => setAutoTradeProfitLimitInr(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
                    />
                    {autoTradeEnabled && sessionPnl > 0 ? (
                      <button
                        type="button"
                        onClick={() => setAutoTradeProfitLimitInr(String(Math.ceil(sessionPnl)))}
                        className="mt-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                      >
                        Use current profit (₹{sessionPnl.toFixed(2)})
                      </button>
                    ) : null}
                  </div>
                  <div>
                    <label htmlFor="at-loss-limit" className="block text-xs font-semibold text-gray-600 mb-1">
                      Max loss (INR)
                    </label>
                    <input
                      id="at-loss-limit"
                      type="number"
                      min={1}
                      step={100}
                      placeholder="e.g. 2000"
                      value={autoTradeLossLimitInr}
                      onChange={(e) => setAutoTradeLossLimitInr(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
                    />
                    {autoTradeEnabled && sessionPnl < 0 ? (
                      <button
                        type="button"
                        onClick={() => setAutoTradeLossLimitInr(String(Math.ceil(Math.abs(sessionPnl))))}
                        className="mt-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
                      >
                        Use current loss (₹{Math.abs(sessionPnl).toFixed(2)})
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">Leave blank to disable a limit.</p>
              </div>

              <div className="pt-1 border-t border-gray-100">
                <div className="block text-xs font-bold text-gray-600 mb-2">Strategies &amp; exits</div>
                <label className="flex items-start gap-2.5 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={Boolean(autoTradeAllStrategies)}
                    onChange={(e) => setAutoTradeAllStrategies(e.target.checked)}
                  />
                  <span className="text-sm font-semibold text-gray-800 leading-snug">
                    All NIFTY signal strategies
                  </span>
                </label>
                {!autoTradeAllStrategies && (
                  <div className="mb-2 max-h-32 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/50 p-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                    {AUTOTRADE_STRATEGY_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 cursor-pointer text-xs sm:text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                          checked={autoTradeSelectedStrategies.includes(opt.value)}
                          onChange={() => toggleAutoTradeStrategy(opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {!autoTradeAllStrategies && autoTradeSelectedStrategies.length === 0 ? (
                  <p className="mt-1 mb-2 text-xs text-amber-700 font-medium">
                    Select at least one strategy, or turn &quot;All&quot; back on.
                  </p>
                ) : null}
                <p className="text-[11px] text-gray-500 mb-2">
                  Set target and stop-loss points per strategy. Trailing uses signal T1/T2/T3 ladder when enabled.
                  {' '}
                  <span className="text-indigo-700 font-medium">
                    MA Ribbon uses signal option targets and NIFTY index SL — not session points.
                  </span>
                </p>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left font-bold px-2 py-1.5">Strategy</th>
                        <th className="text-left font-bold px-1 py-1.5 w-14">Tgt</th>
                        <th className="text-left font-bold px-1 py-1.5 w-14">SL</th>
                        <th className="text-center font-bold px-1 py-1.5 w-12">Trail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {AUTOTRADE_STRATEGY_OPTIONS.filter((opt) =>
                        autoTradeAllStrategies ? true : autoTradeSelectedStrategies.includes(opt.value),
                      ).map((opt) => {
                        const isMaRibbon = opt.value === 'ma_ribbon_cloud_atr';
                        const cfg = autoTradeStrategyExitConfig[opt.value] || {
                          target_points: 10,
                          stop_loss_points: 10,
                          trailing_enabled: false,
                        };
                        return (
                          <tr key={opt.value} className={`border-t border-gray-100${isMaRibbon ? ' bg-indigo-50/40' : ''}`}>
                            <td className="px-2 py-1.5 text-gray-800 font-medium leading-tight">
                              {opt.label}
                              {isMaRibbon ? (
                                <span className="mt-0.5 block text-[10px] font-normal text-indigo-700 leading-snug">
                                  Signal exits only
                                </span>
                              ) : null}
                            </td>
                            <td className="px-1 py-1">
                              {isMaRibbon ? (
                                <span
                                  className="block w-full px-1 py-1.5 rounded border border-indigo-200 bg-indigo-50 text-[10px] font-bold text-indigo-800 text-center leading-tight"
                                  title="Profit target comes from the MA Ribbon signal option target (T1/T2/T3), not session points."
                                >
                                  Option T1
                                </span>
                              ) : (
                              <input
                                type="number"
                                min={1}
                                max={500}
                                value={cfg.target_points}
                                disabled={autoTradeExitSource === 'signal_targets'}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10) || 10;
                                  updateStrategyExitField(opt.value, 'target_points', Math.max(1, Math.min(500, v)));
                                }}
                                className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs font-semibold disabled:bg-gray-50"
                              />
                              )}
                            </td>
                            <td className="px-1 py-1">
                              {isMaRibbon ? (
                                <span
                                  className="block w-full px-1 py-1.5 rounded border border-indigo-200 bg-indigo-50 text-[10px] font-bold text-indigo-800 text-center leading-tight"
                                  title="Stop-loss exit when NIFTY crosses the signal index SL. Session SL points are not used."
                                >
                                  Index SL
                                </span>
                              ) : (
                              <input
                                type="number"
                                min={1}
                                max={500}
                                value={cfg.stop_loss_points}
                                disabled={autoTradeExitSource === 'signal_targets'}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10) || 10;
                                  updateStrategyExitField(opt.value, 'stop_loss_points', Math.max(1, Math.min(500, v)));
                                }}
                                className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs font-semibold disabled:bg-gray-50"
                              />
                              )}
                            </td>
                            <td className="px-1 py-1 text-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                checked={Boolean(cfg.trailing_enabled)}
                                onChange={(e) =>
                                  updateStrategyExitField(opt.value, 'trailing_enabled', e.target.checked)
                                }
                                title={
                                  isMaRibbon
                                    ? 'MA Ribbon trailing: moves option SL/target on T1/T2/T3; exit still on index SL'
                                    : 'Trailing stop-loss (T1/T2/T3 ladder)'
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-1 border-t border-gray-100">
                <div className="block text-xs font-bold text-gray-600 mb-2">Exit Settings</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="at-exit-source" className="block text-xs font-semibold text-gray-600 mb-1">
                      Exit Source
                    </label>
                    <select
                      id="at-exit-source"
                      value={autoTradeExitSource}
                      onChange={(e) => setAutoTradeExitSource(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                    >
                      <option value="session_points">Session Points</option>
                      <option value="signal_targets">Signal Targets</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="at-signal-target-key" className="block text-xs font-semibold text-gray-600 mb-1">
                      Signal Target
                    </label>
                    <select
                      id="at-signal-target-key"
                      value={autoTradeSignalTargetKey}
                      onChange={(e) => setAutoTradeSignalTargetKey(e.target.value)}
                      disabled={autoTradeExitSource !== 'signal_targets'}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="target_1">Target 1</option>
                      <option value="target_2">Target 2</option>
                      <option value="target_3">Target 3</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <div>
                    <label htmlFor="at-custom-target" className="block text-xs font-semibold text-gray-600 mb-1">
                      Custom Target Price (optional)
                    </label>
                    <input
                      id="at-custom-target"
                      type="number"
                      min={0}
                      step="0.05"
                      value={autoTradeCustomTargetPrice}
                      onChange={(e) => setAutoTradeCustomTargetPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
                    />
                  </div>
                  <div>
                    <label htmlFor="at-custom-sl" className="block text-xs font-semibold text-gray-600 mb-1">
                      Custom Stop-Loss Price (optional)
                    </label>
                    <input
                      id="at-custom-sl"
                      type="number"
                      min={0}
                      step="0.05"
                      value={autoTradeCustomStopLossPrice}
                      onChange={(e) => setAutoTradeCustomStopLossPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold"
                    />
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                  If both custom prices are set, they override both session points and signal-target mapping.
                </p>
              </div>

              <div>
                <label htmlFor="at-strike" className="block text-xs font-bold text-gray-600 mb-1">
                  Strike Preference
                </label>
                <select
                  id="at-strike"
                  value={autoTradeStrikeMode}
                  onChange={(e) => setAutoTradeStrikeMode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  <option value="ATM">ATM (Normal)</option>
                  <option value="DEEP_OTM">Deep OTM (Cheapest, Test Mode)</option>
                  <option value="DEEP_ITM">Deep ITM</option>
                </select>
              </div>
              <div>
                <label htmlFor="at-expiry" className="block text-xs font-bold text-gray-600 mb-1">
                  Expiry
                </label>
                <select
                  id="at-expiry"
                  value={autoTradeExpiryChoice}
                  onChange={(e) => setAutoTradeExpiryChoice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  <option value="AUTO">AUTO (use signal expiry)</option>
                  <option value="CURRENT_WEEK">CURRENT WEEK</option>
                  <option value="NEXT_WEEK">NEXT WEEK</option>
                  <option value="MONTHLY">MONTHLY</option>
                </select>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-gray-500">
              {autoTradeEnabled
                ? 'Changes save immediately while the session is active. Open orders with unchanged trail/breakeven levels are refreshed from the new per-strategy points.'
                : 'Configure before activation, or save while a session is running to apply mid-session.'}
            </p>

            {configSaveMessage && !configOpen ? (
              <p className="mt-2 text-xs text-emerald-700 font-medium text-center">{configSaveMessage}</p>
            ) : null}

            <button
              type="button"
              onClick={handleConfigDone}
              disabled={configSaving}
              className="mt-4 w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
            >
              {configSaving ? 'Saving…' : autoTradeEnabled ? 'Save configuration' : 'Done'}
            </button>
          </div>
        </div>
      ) : null}

      <AutoTradeDisabledModal
        isOpen={showAutoTradeDisabledModal}
        onClose={() => setShowAutoTradeDisabledModal(false)}
        reason={autoTradeDisabledReason}
        ctaLabel={autoTradeModalCtaLabel}
        onCtaClick={autoTradeModalCtaLabel ? () => navigate('/pricing') : null}
      />
    </div>
  );
}

export default AutoTradePanel;
